import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import AssessmentMetrics from './components/AssessmentMetrics';
import ActiveAssessments from './components/ActiveAssessments';
import AssessmentBuilder from './components/AssessmentBuilder';
import ResultsDashboard from './components/ResultsDashboard';
import ProctorMonitoring from './components/ProctorMonitoring';
import CertificationGenerator from './components/CertificationGenerator';

const TrainingAssessmentCenter = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  const assessmentMetrics = {
    totalAssessments: 89,
    activeAssessments: 23,
    completedToday: 45,
    averageScore: 84.2,
    passRate: 89.2,
    pendingGrading: 12,
    certificationsIssued: 156,
    remedialsScheduled: 8
  };

  const activeAssessments = [
    {
      id: 1,
      title: 'Safety Orientation Assessment',
      type: 'mandatory',
      status: 'active',
      participants: 45,
      completed: 32,
      duration: 30,
      passingScore: 80,
      questions: 25,
      deadline: '12/15/2025'
    },
    {
      id: 2,
      title: 'Forklift Operator Certification',
      type: 'certification',
      status: 'active',
      participants: 12,
      completed: 8,
      duration: 45,
      passingScore: 85,
      questions: 40,
      deadline: '12/20/2025'
    },
    {
      id: 3,
      title: 'Hazardous Materials Handling',
      type: 'skill-based',
      status: 'active',
      participants: 28,
      completed: 15,
      duration: 60,
      passingScore: 80,
      questions: 35,
      deadline: '12/18/2025'
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'assessments', label: 'Active Assessments', icon: 'ClipboardList' },
    { id: 'builder', label: 'Assessment Builder', icon: 'Edit' },
    { id: 'results', label: 'Results & Analytics', icon: 'BarChart3' },
    { id: 'proctoring', label: 'Proctoring', icon: 'Video' },
    { id: 'certifications', label: 'Certifications', icon: 'Award' }
  ];

  const handleCreateAssessment = () => {
    setActiveTab('builder');
  };

  const handleViewResults = (assessment) => {
    setSelectedAssessment(assessment);
    setActiveTab('results');
  };

  const handleBackToDashboard = () => {
    navigate('/training-compliance-dashboard');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToDashboard}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Training Assessment Center</h1>
                <p className="text-muted-foreground">
                  Comprehensive evaluation tools for competency testing, certification assessments, and skill validation
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCreateAssessment}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
              >
                <Icon name="Plus" size={20} />
                Create Assessment
              </button>
              <button className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg hover:bg-muted transition-colors">
                <Icon name="Download" size={20} />
                Export Report
              </button>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="flex items-center gap-2 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Content Area */}
      <div className="p-6 lg:p-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <AssessmentMetrics metrics={assessmentMetrics} />
            <ActiveAssessments 
              assessments={activeAssessments}
              onViewResults={handleViewResults}
            />
          </div>
        )}

        {activeTab === 'assessments' && (
          <ActiveAssessments 
            assessments={activeAssessments}
            onViewResults={handleViewResults}
            detailed={true}
          />
        )}

        {activeTab === 'builder' && (
          <AssessmentBuilder />
        )}

        {activeTab === 'results' && (
          <ResultsDashboard selectedAssessment={selectedAssessment} />
        )}

        {activeTab === 'proctoring' && (
          <ProctorMonitoring />
        )}

        {activeTab === 'certifications' && (
          <CertificationGenerator />
        )}
      </div>
    </div>
  );
};

export default TrainingAssessmentCenter;